import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllSubjectsWithStats,
  createSubjectGlobally,
  updateSubjectById,
  fetchModulesBySubject,
  createModuleForSubject,
  updateModuleById,
  deleteModuleById,
  reorderModules,
  fetchTopicsByModule,
  createTopicForModule,
  updateTopicById,
  deleteTopicById,
  reorderTopics,
  fetchSections,
  fetchSectionById,
  createSection,
  updateSection,
  deleteSection,
  reorderSections,
  updateSectionNotesText,
  addQuestionToSection,
  removeQuestionFromSection,
} from "../../pages/services/notesService";
import ConfirmationDialog from "../ReusableComponent/ConfirmationDialog";
import AssignmentQuestionPanel from "../ReusableComponent/AssignmentQuestionPanel";
import RichTextEditor from "../ReusableComponent/RichTextEditor";
import "../../styles/NotesNavigator.css";

const SECTION_TYPES = [
  { value: "notes",      label: "Notes",             icon: "📝" },
  { value: "example",   label: "Example Questions",  icon: "💡" },
  { value: "quiz",      label: "Quiz",               icon: "🎯" },
  { value: "practice",  label: "Practice Questions", icon: "📚" },
  { value: "assignment",label: "Assignment",         icon: "📋" },
];

const TYPE_COLORS = {
  notes:      { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  example:    { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  quiz:       { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  practice:   { bg: "#fdf4ff", color: "#7e22ce", border: "#e9d5ff" },
  assignment: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
};

const DIFF_COLORS = {
  easy:         { bg: "#dcfce7", color: "#16a34a" },
  medium:       { bg: "#fef3c7", color: "#d97706" },
  hard:         { bg: "#fee2e2", color: "#dc2626" },
  "very-hard":  { bg: "#fae8ff", color: "#9333ea" },
  professional: { bg: "#e0f2fe", color: "#0284c7" },
};

const typeInfo = (type) => SECTION_TYPES.find(t => t.value === type) || { label: type, icon: "" };

const TypeBadge = ({ type }) => {
  const info = typeInfo(type);
  const c = TYPE_COLORS[type] || { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" };
  return (
    <span className="nn-type-badge" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
      {info.icon} {info.label}
    </span>
  );
};

const DiffBadge = ({ level }) => {
  if (!level) return null;
  const key = level.toLowerCase().replace(/\s+/g, "-");
  const c = DIFF_COLORS[key] || { bg: "#f1f5f9", color: "#475569" };
  return (
    <span className="nn-diff-badge" style={{ background: c.bg, color: c.color }}>
      {level}
    </span>
  );
};

// Click to copy the full MongoDB ID; shows last 8 chars as preview
const IdBadge = ({ id }) => {
  const [flash, setFlash] = useState(false);
  if (!id) return null;
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(id).catch(() => {});
    setFlash(true);
    setTimeout(() => setFlash(false), 1200);
  };
  return (
    <span
      className={`nn-id-badge${flash ? " copied" : ""}`}
      title={`Full ID: ${id}\nClick to copy`}
      onClick={copy}
    >
      {flash ? "Copied!" : `#${id.slice(-8)}`}
    </span>
  );
};

// ── Reorder helper ────────────────────────────────────────────────────────────
function reorder(arr, fromIdx, toIdx) {
  const next = [...arr];
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
}

const NotesManagementPage = () => {
  // ── Data ──────────────────────────────────────────────────────────────────────
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [sections, setSections] = useState([]);

  // ── Loading ───────────────────────────────────────────────────────────────────
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);

  // ── Section detail (inline — keeps sidebar visible) ───────────────────────────
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionNotesText, setSectionNotesText] = useState("");
  const notesEditorRef = useRef(null); // ref to RichTextEditor — read .getHTML() at save time
  const [savingNotes, setSavingNotes] = useState(false);
  const [savedNoteOk, setSavedNoteOk] = useState(false);
  const [loadingSectionDetail, setLoadingSectionDetail] = useState(false);
  const [sectionQSliderOpen, setSectionQSliderOpen] = useState(false);

  // ── Subject CRUD ─────────────────────────────────────────────────────────────
  const [addingSubject, setAddingSubject] = useState(false);
  const [addSubjectTitle, setAddSubjectTitle] = useState("");
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editSubjectTitle, setEditSubjectTitle] = useState("");

  // ── Module CRUD ───────────────────────────────────────────────────────────────
  const [addingModuleActive, setAddingModuleActive] = useState(false);
  const [addingModuleTitle, setAddingModuleTitle] = useState("");
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editModuleTitle, setEditModuleTitle] = useState("");
  const [busyModuleId, setBusyModuleId] = useState(null);

  // ── Topic CRUD ────────────────────────────────────────────────────────────────
  const [addingTopicModuleId, setAddingTopicModuleId] = useState(null);
  const [addingTopicTitle, setAddingTopicTitle] = useState("");
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editTopicTitle, setEditTopicTitle] = useState("");
  const [busyTopicId, setBusyTopicId] = useState(null);

  // ── Section CRUD ──────────────────────────────────────────────────────────────
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({ title: "", type: "notes" });
  const [sectionFormError, setSectionFormError] = useState("");
  const [busySection, setBusySection] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editSectionTitle, setEditSectionTitle] = useState("");
  const [editSectionError, setEditSectionError] = useState("");

  // ── Insert above/below ────────────────────────────────────────────────────────
  const [insertModule, setInsertModule] = useState(null);
  const [insertModuleTitle, setInsertModuleTitle] = useState("");
  const [insertTopic, setInsertTopic] = useState(null);
  const [insertTopicTitle, setInsertTopicTitle] = useState("");
  const [sectionInsertAt, setSectionInsertAt] = useState(null);

  // ── DnD state ─────────────────────────────────────────────────────────────────
  const [draggedModuleId, setDraggedModuleId] = useState(null);
  const [dragOverModuleId, setDragOverModuleId] = useState(null);
  const [draggedTopicId, setDraggedTopicId] = useState(null);
  const [draggedTopicModuleId, setDraggedTopicModuleId] = useState(null);
  const [dragOverTopicId, setDragOverTopicId] = useState(null);
  const [draggedSectionId, setDraggedSectionId] = useState(null);
  const [dragOverSectionId, setDragOverSectionId] = useState(null);

  // ── Confirm + Toast ───────────────────────────────────────────────────────────
  const [confirm, setConfirm] = useState({ show: false, message: "", onConfirm: null });
  const [toast, setToast] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => { loadSubjects(); }, []);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  const loadSubjects = async () => {
    setLoadingSubjects(true);
    try {
      const data = await fetchAllSubjectsWithStats({ pageSize: 100 });
      setSubjects(data.data || data.subjects || data || []);
    } catch {
      setSubjects([]);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const loadModulesForSubject = async (subjectId) => {
    setLoadingModules(true);
    setModules([]);
    try {
      const raw = await fetchModulesBySubject(subjectId);
      setModules((raw || []).map(m => ({ ...m, _open: false, _topicsLoading: false, _topics: null })));
    } catch {
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  };

  const loadTopicsForModule = async (moduleId) => {
    setModules(prev => prev.map(m => m._id === moduleId ? { ...m, _topicsLoading: true } : m));
    try {
      const topics = await fetchTopicsByModule(moduleId);
      setModules(prev => prev.map(m =>
        m._id === moduleId ? { ...m, _topics: topics || [], _topicsLoading: false } : m
      ));
    } catch {
      setModules(prev => prev.map(m =>
        m._id === moduleId ? { ...m, _topics: [], _topicsLoading: false } : m
      ));
    }
  };

  const loadSectionsForTopic = async (topicId) => {
    setLoadingSections(true);
    try {
      const raw = await fetchSections(topicId);
      setSections(raw || []);
    } catch {
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  };

  // ── Subject selection ─────────────────────────────────────────────────────────
  const handleSelectSubject = (subjectId) => {
    if (subjectId === selectedSubjectId) return;
    setSelectedSubjectId(subjectId);
    setSelectedTopicId(null);
    setSelectedTopic(null);
    setSections([]);
    setSelectedSection(null);
    setSectionNotesText("");
    setEditingModuleId(null);
    setEditingTopicId(null);
    setAddingModuleActive(false);
    setAddingTopicModuleId(null);
    setInsertModule(null);
    setInsertTopic(null);
    loadModulesForSubject(subjectId);
  };

  // ── Module accordion ──────────────────────────────────────────────────────────
  const handleModuleToggle = (mod) => {
    const nowOpen = !mod._open;
    setModules(prev => prev.map(m => m._id === mod._id ? { ...m, _open: nowOpen } : m));
    if (nowOpen && mod._topics === null) loadTopicsForModule(mod._id);
  };

  // ── Topic selection ───────────────────────────────────────────────────────────
  const handleTopicClick = (topic, mod) => {
    if (topic._id === selectedTopicId) return;
    setSelectedTopicId(topic._id);
    setSelectedTopic({
      _id: topic._id,
      title: topic.title,
      moduleId: mod._id,
      moduleName: mod.title,
      subjectName: subjects.find(s => s._id === selectedSubjectId)?.title || "",
    });
    setSelectedSection(null);
    setSectionNotesText("");
    loadSectionsForTopic(topic._id);
  };

  // ── Section detail (inline) ───────────────────────────────────────────────────
  const handleOpenSection = async (section) => {
    setLoadingSectionDetail(true);
    try {
      const data = await fetchSectionById(section._id);
      setSelectedSection(data);
      setSectionNotesText(data?.notesText || "");
    } catch {
      showToast("Failed to load section");
    } finally {
      setLoadingSectionDetail(false);
    }
  };

  const handleBackToSections = () => {
    setSelectedSection(null);
    setSectionNotesText("");
    setSavedNoteOk(false);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    setSavedNoteOk(false);
    try {
      // Read directly from the TipTap editor via ref — guaranteed to have the latest
      // content including async image insertions (paste, drag-drop, file picker).
      const content = notesEditorRef.current?.getHTML() ?? sectionNotesText;
      await updateSectionNotesText(selectedSection._id, content);
      setSavedNoteOk(true);
      setTimeout(() => setSavedNoteOk(false), 2500);
    } catch {
      showToast("Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleConfirmSectionQuestions = async (selectedIds, removedIds) => {
    const existingIds = (selectedSection?.questions || []).map(q => q._id);
    const toAdd = selectedIds.filter(id => !existingIds.includes(id));
    try {
      let updated = selectedSection;
      for (const id of toAdd) {
        const res = await addQuestionToSection(selectedSection._id, id);
        updated = res.data;
      }
      for (const id of removedIds) {
        const res = await removeQuestionFromSection(selectedSection._id, id);
        updated = res.data;
      }
      setSelectedSection(updated);
      showToast("Questions updated");
    } catch {
      showToast("Error updating questions");
    }
  };

  const handleRemoveSectionQuestion = async (questionId) => {
    try {
      const res = await removeQuestionFromSection(selectedSection._id, questionId);
      setSelectedSection(res.data);
      showToast("Question removed");
    } catch {
      showToast("Error removing question");
    }
  };

  // ── Subject CRUD ─────────────────────────────────────────────────────────────
  const handleAddSubjectSave = async () => {
    const title = addSubjectTitle.trim();
    if (!title) return;
    try {
      await createSubjectGlobally(title);
      setAddingSubject(false); setAddSubjectTitle("");
      await loadSubjects();
      showToast("Subject created");
    } catch { showToast("Failed to create subject"); }
  };

  const handleEditSubjectSave = async () => {
    const title = editSubjectTitle.trim();
    if (!title || !editingSubjectId) return;
    try {
      await updateSubjectById(editingSubjectId, title);
      setEditingSubjectId(null); setEditSubjectTitle("");
      await loadSubjects();
      if (selectedTopic) setSelectedTopic(prev => ({ ...prev, subjectName: title }));
      showToast("Subject renamed");
    } catch { showToast("Failed to rename subject"); }
  };

  // ── Module CRUD ───────────────────────────────────────────────────────────────
  const handleAddModuleSave = async () => {
    const title = addingModuleTitle.trim();
    if (!title || !selectedSubjectId) return;
    try {
      await createModuleForSubject(selectedSubjectId, title);
      setAddingModuleActive(false); setAddingModuleTitle("");
      await loadModulesForSubject(selectedSubjectId);
      showToast("Module created");
    } catch { showToast("Failed to create module"); }
  };

  const handleEditModuleSave = async (mod) => {
    const title = editModuleTitle.trim();
    if (!title) return;
    setBusyModuleId(mod._id);
    try {
      await updateModuleById(selectedSubjectId, mod._id, title);
      setModules(prev => prev.map(m => m._id === mod._id ? { ...m, title } : m));
      if (selectedTopic?.moduleId === mod._id) setSelectedTopic(prev => ({ ...prev, moduleName: title }));
      setEditingModuleId(null);
      showToast("Module renamed");
    } catch { showToast("Failed to rename module"); }
    finally { setBusyModuleId(null); }
  };

  const handleDeleteModule = (mod) => {
    setConfirm({
      show: true,
      message: `Delete module "${mod.title}" and all its topics?`,
      onConfirm: async () => {
        setConfirm(c => ({ ...c, show: false }));
        try {
          await deleteModuleById(selectedSubjectId, mod._id);
          setModules(prev => prev.filter(m => m._id !== mod._id));
          if (selectedTopic?.moduleId === mod._id) {
            setSelectedTopicId(null); setSelectedTopic(null); setSections([]);
            setSelectedSection(null); setSectionNotesText("");
          }
          showToast("Module deleted");
        } catch { showToast("Failed to delete module"); }
      },
    });
  };

  const handleInsertModuleSave = async () => {
    const title = insertModuleTitle.trim();
    if (!title || !insertModule) return;
    try {
      const res = await createModuleForSubject(selectedSubjectId, title);
      const newMod = res.data;
      const targetIdx = modules.findIndex(m => m._id === insertModule.targetId);
      const insertIdx = insertModule.mode === "above" ? targetIdx : targetIdx + 1;
      const orderedIds = modules.map(m => m._id);
      orderedIds.splice(insertIdx, 0, newMod._id);
      await reorderModules(selectedSubjectId, orderedIds);
      setInsertModule(null); setInsertModuleTitle("");
      await loadModulesForSubject(selectedSubjectId);
      showToast("Module created");
    } catch { showToast("Failed to create module"); }
  };

  // ── Topic CRUD ────────────────────────────────────────────────────────────────
  const handleAddTopicSave = async (moduleId) => {
    const title = addingTopicTitle.trim();
    if (!title) return;
    try {
      await createTopicForModule(moduleId, title);
      setAddingTopicModuleId(null); setAddingTopicTitle("");
      await loadTopicsForModule(moduleId);
      showToast("Topic created");
    } catch { showToast("Failed to create topic"); }
  };

  const handleEditTopicSave = async (topic, moduleId) => {
    const title = editTopicTitle.trim();
    if (!title) return;
    setBusyTopicId(topic._id);
    try {
      await updateTopicById(moduleId, topic._id, title);
      setModules(prev => prev.map(m =>
        m._id === moduleId
          ? { ...m, _topics: (m._topics || []).map(t => t._id === topic._id ? { ...t, title } : t) }
          : m
      ));
      if (selectedTopicId === topic._id) setSelectedTopic(prev => ({ ...prev, title }));
      setEditingTopicId(null);
      showToast("Topic renamed");
    } catch { showToast("Failed to rename topic"); }
    finally { setBusyTopicId(null); }
  };

  const handleDeleteTopic = (topic, moduleId) => {
    setConfirm({
      show: true,
      message: `Delete topic "${topic.title}" and all its sections?`,
      onConfirm: async () => {
        setConfirm(c => ({ ...c, show: false }));
        try {
          await deleteTopicById(moduleId, topic._id);
          setModules(prev => prev.map(m =>
            m._id === moduleId
              ? { ...m, _topics: (m._topics || []).filter(t => t._id !== topic._id) }
              : m
          ));
          if (selectedTopicId === topic._id) {
            setSelectedTopicId(null); setSelectedTopic(null); setSections([]);
            setSelectedSection(null); setSectionNotesText("");
          }
          showToast("Topic deleted");
        } catch { showToast("Failed to delete topic"); }
      },
    });
  };

  const handleInsertTopicSave = async () => {
    const title = insertTopicTitle.trim();
    if (!title || !insertTopic) return;
    const { targetId, moduleId, mode } = insertTopic;
    try {
      const res = await createTopicForModule(moduleId, title);
      const newTopic = res.data;
      const mod = modules.find(m => m._id === moduleId);
      const topics = mod?._topics || [];
      const targetIdx = topics.findIndex(t => t._id === targetId);
      const insertIdx = mode === "above" ? targetIdx : targetIdx + 1;
      const orderedIds = topics.map(t => t._id);
      orderedIds.splice(insertIdx, 0, newTopic._id);
      await reorderTopics(moduleId, orderedIds);
      setInsertTopic(null); setInsertTopicTitle("");
      await loadTopicsForModule(moduleId);
      showToast("Topic created");
    } catch { showToast("Failed to create topic"); }
  };

  // ── Section CRUD ──────────────────────────────────────────────────────────────
  const handleAddSectionSave = async () => {
    const title = sectionForm.title.trim();
    if (!title) { setSectionFormError("Title is required"); return; }
    setBusySection(true);
    try {
      const res = await createSection(selectedTopicId, { title, type: sectionForm.type });
      const newSection = res.data;
      if (sectionInsertAt) {
        const { mode, targetId } = sectionInsertAt;
        const targetIdx = sections.findIndex(s => s._id === targetId);
        const insertIdx = mode === "above" ? targetIdx : targetIdx + 1;
        const orderedIds = sections.map(s => s._id);
        orderedIds.splice(insertIdx, 0, newSection._id);
        await reorderSections(selectedTopicId, orderedIds);
        setSectionInsertAt(null);
      }
      setAddSectionOpen(false);
      setSectionForm({ title: "", type: "notes" });
      setSectionFormError("");
      await loadSectionsForTopic(selectedTopicId);
      showToast("Section created");
    } catch { showToast("Failed to create section"); }
    finally { setBusySection(false); }
  };

  const handleRenameSectionSave = async () => {
    const title = editSectionTitle.trim();
    if (!title) { setEditSectionError("Title is required"); return; }
    setBusySection(true);
    try {
      await updateSection(editingSectionId, { title });
      setSections(prev => prev.map(s => s._id === editingSectionId ? { ...s, title } : s));
      if (selectedSection?._id === editingSectionId) {
        setSelectedSection(prev => ({ ...prev, title }));
      }
      setEditingSectionId(null); setEditSectionTitle(""); setEditSectionError("");
      showToast("Section renamed");
    } catch { showToast("Failed to rename section"); }
    finally { setBusySection(false); }
  };

  const handleDeleteSection = (section) => {
    setConfirm({
      show: true,
      message: `Delete section "${section.title}"? All content inside will be lost.`,
      onConfirm: async () => {
        setConfirm(c => ({ ...c, show: false }));
        try {
          await deleteSection(section._id);
          setSections(prev => prev.filter(s => s._id !== section._id));
          if (selectedSection?._id === section._id) {
            setSelectedSection(null); setSectionNotesText("");
          }
          showToast("Section deleted");
        } catch { showToast("Failed to delete section"); }
      },
    });
  };

  // ── Module DnD ────────────────────────────────────────────────────────────────
  const handleModuleDragStart = (e, modId) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("moduleId", modId);
    setDraggedModuleId(modId);
    setInsertModule(null);
  };
  const handleModuleContainerDragOver = (e, modId) => {
    if (!draggedModuleId || draggedModuleId === modId) return;
    e.preventDefault();
    setDragOverModuleId(modId);
  };
  const handleModuleContainerDrop = async (e, targetModId) => {
    if (!draggedModuleId) return;
    e.preventDefault();
    setDragOverModuleId(null);
    if (draggedModuleId === targetModId) return;
    const from = modules.findIndex(m => m._id === draggedModuleId);
    const to   = modules.findIndex(m => m._id === targetModId);
    if (from < 0 || to < 0) return;
    const reordered = reorder(modules, from, to);
    setModules(reordered);
    try {
      await reorderModules(selectedSubjectId, reordered.map(m => m._id));
    } catch {
      showToast("Failed to save order");
      loadModulesForSubject(selectedSubjectId);
    }
  };
  const handleModuleDragEnd = () => { setDraggedModuleId(null); setDragOverModuleId(null); };

  // ── Topic DnD ─────────────────────────────────────────────────────────────────
  const handleTopicDragStart = (e, topicId, moduleId) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("topicId", topicId);
    setDraggedTopicId(topicId);
    setDraggedTopicModuleId(moduleId);
    setInsertTopic(null);
  };
  const handleTopicDragOver = (e, topicId, moduleId) => {
    if (!draggedTopicId || draggedTopicModuleId !== moduleId || draggedTopicId === topicId) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverTopicId(topicId);
  };
  const handleTopicDrop = async (e, targetTopicId, moduleId) => {
    if (!draggedTopicId) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverTopicId(null);
    if (draggedTopicId === targetTopicId || draggedTopicModuleId !== moduleId) return;
    const mod = modules.find(m => m._id === moduleId);
    const topics = mod?._topics || [];
    const from = topics.findIndex(t => t._id === draggedTopicId);
    const to   = topics.findIndex(t => t._id === targetTopicId);
    if (from < 0 || to < 0) return;
    const reordered = reorder(topics, from, to);
    setModules(prev => prev.map(m => m._id === moduleId ? { ...m, _topics: reordered } : m));
    try {
      await reorderTopics(moduleId, reordered.map(t => t._id));
    } catch {
      showToast("Failed to save order");
      loadTopicsForModule(moduleId);
    }
  };
  const handleTopicDragEnd = () => { setDraggedTopicId(null); setDraggedTopicModuleId(null); setDragOverTopicId(null); };

  // ── Section DnD ───────────────────────────────────────────────────────────────
  const handleSectionDragStart = (e, sectionId) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("sectionId", sectionId);
    setDraggedSectionId(sectionId);
    setSectionInsertAt(null);
  };
  const handleSectionDragOver = (e, sectionId) => {
    if (!draggedSectionId || draggedSectionId === sectionId) return;
    e.preventDefault();
    setDragOverSectionId(sectionId);
  };
  const handleSectionDrop = async (e, targetSectionId) => {
    if (!draggedSectionId) return;
    e.preventDefault();
    setDragOverSectionId(null);
    if (draggedSectionId === targetSectionId) return;
    const from = sections.findIndex(s => s._id === draggedSectionId);
    const to   = sections.findIndex(s => s._id === targetSectionId);
    if (from < 0 || to < 0) return;
    const reordered = reorder(sections, from, to);
    setSections(reordered);
    try {
      await reorderSections(selectedTopicId, reordered.map(s => s._id));
    } catch {
      showToast("Failed to save order");
      loadSectionsForTopic(selectedTopicId);
    }
  };
  const handleSectionDragEnd = () => { setDraggedSectionId(null); setDragOverSectionId(null); };

  // ── Cancel helpers ────────────────────────────────────────────────────────────
  const cancelAddSubject    = () => { setAddingSubject(false); setAddSubjectTitle(""); };
  const cancelEditSubject   = () => { setEditingSubjectId(null); setEditSubjectTitle(""); };
  const cancelAddModule     = () => { setAddingModuleActive(false); setAddingModuleTitle(""); };
  const cancelInsertModule  = () => { setInsertModule(null); setInsertModuleTitle(""); };
  const cancelAddTopic      = () => { setAddingTopicModuleId(null); setAddingTopicTitle(""); };
  const cancelInsertTopic   = () => { setInsertTopic(null); setInsertTopicTitle(""); };
  const cancelAddSection    = () => {
    setAddSectionOpen(false);
    setSectionForm({ title: "", type: "notes" });
    setSectionFormError("");
    setSectionInsertAt(null);
  };
  const cancelRenameSection = () => { setEditingSectionId(null); setEditSectionTitle(""); setEditSectionError(""); };

  const openInsertSectionModal = (mode, targetId) => {
    setSectionInsertAt({ mode, targetId });
    setSectionForm({ title: "", type: "notes" });
    setSectionFormError("");
    setAddSectionOpen(true);
  };

  const selectedSubject = subjects.find(s => s._id === selectedSubjectId);

  // ── Inline insert forms ───────────────────────────────────────────────────────
  const InsertModuleForm = () => (
    <div className="nn-insert-form">
      <input
        autoFocus
        placeholder="New module name…"
        value={insertModuleTitle}
        onChange={e => setInsertModuleTitle(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") handleInsertModuleSave(); if (e.key === "Escape") cancelInsertModule(); }}
      />
      <button className="nn-inline-save" onClick={handleInsertModuleSave}>Save</button>
      <button className="nn-inline-cancel" onClick={cancelInsertModule}>✕</button>
    </div>
  );

  const InsertTopicForm = () => (
    <div className="nn-insert-form for-topic">
      <input
        autoFocus
        placeholder="New topic name…"
        value={insertTopicTitle}
        onChange={e => setInsertTopicTitle(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") handleInsertTopicSave(); if (e.key === "Escape") cancelInsertTopic(); }}
      />
      <button className="nn-inline-save" onClick={handleInsertTopicSave}>Save</button>
      <button className="nn-inline-cancel" onClick={cancelInsertTopic}>✕</button>
    </div>
  );

  // ── Derived ───────────────────────────────────────────────────────────────────
  const sectionQuestions = selectedSection?.questions || [];
  const sectionQIds = sectionQuestions.map(q => q._id);

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="nn-page">

      {/* ── LEFT SIDEBAR ──────────────────────────────────────────── */}
      <div className="nn-sidebar">

        {/* Subject selector */}
        <div className="nn-sidebar-top">
          <p className="nn-sidebar-title">Subjects</p>
          {loadingSubjects ? (
            <div className="nn-tree-loading">Loading subjects…</div>
          ) : (
            <>
              <div className="nn-subject-tabs">
                {subjects.map(s => (
                  <div
                    key={s._id}
                    className={`nn-subject-tab ${selectedSubjectId === s._id ? "active" : ""}`}
                    onClick={() => handleSelectSubject(s._id)}
                    title={s.title}
                  >
                    <span className="nn-subject-tab-name">{s.title}</span>
                    {selectedSubjectId === s._id && <IdBadge id={s._id} />}
                  </div>
                ))}
              </div>

              <div className="nn-subject-actions">
                {addingSubject ? (
                  <div className="nn-inline-form" style={{ flex: 1 }}>
                    <input
                      className="nn-inline-input"
                      autoFocus
                      placeholder="Subject name…"
                      value={addSubjectTitle}
                      onChange={e => setAddSubjectTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") handleAddSubjectSave(); if (e.key === "Escape") cancelAddSubject(); }}
                    />
                    <div className="nn-inline-row">
                      <button className="nn-inline-save" onClick={handleAddSubjectSave}>Save</button>
                      <button className="nn-inline-cancel" onClick={cancelAddSubject}>Cancel</button>
                    </div>
                  </div>
                ) : editingSubjectId ? (
                  <div className="nn-inline-form" style={{ flex: 1 }}>
                    <input
                      className="nn-inline-input"
                      autoFocus
                      value={editSubjectTitle}
                      onChange={e => setEditSubjectTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") handleEditSubjectSave(); if (e.key === "Escape") cancelEditSubject(); }}
                    />
                    <div className="nn-inline-row">
                      <button className="nn-inline-save" onClick={handleEditSubjectSave}>Save</button>
                      <button className="nn-inline-cancel" onClick={cancelEditSubject}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button className="nn-add-subject-btn" onClick={() => { setAddingSubject(true); setEditingSubjectId(null); }}>
                      + Add Subject
                    </button>
                    {selectedSubjectId && (
                      <button
                        className="nn-edit-subject-btn"
                        title="Rename selected subject"
                        onClick={() => { setEditingSubjectId(selectedSubjectId); setEditSubjectTitle(selectedSubject?.title || ""); setAddingSubject(false); }}
                      >
                        ✏️
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Module + Topic tree */}
        <div className="nn-tree">
          {!selectedSubjectId ? (
            <div className="nn-tree-loading">Select a subject above</div>
          ) : loadingModules ? (
            <div className="nn-tree-loading">Loading modules…</div>
          ) : (
            <>
              {modules.length === 0 && !addingModuleActive && (
                <div className="nn-tree-loading">No modules yet.</div>
              )}

              {modules.map((mod) => (
                <React.Fragment key={mod._id}>
                  {insertModule?.mode === "above" && insertModule.targetId === mod._id && <InsertModuleForm />}

                  <div
                    className={[
                      "nn-module-group",
                      draggedModuleId === mod._id ? "nn-dragging" : "",
                      dragOverModuleId === mod._id ? "nn-drag-over" : "",
                    ].join(" ")}
                    onDragOver={e => handleModuleContainerDragOver(e, mod._id)}
                    onDragLeave={() => { if (dragOverModuleId === mod._id) setDragOverModuleId(null); }}
                    onDrop={e => handleModuleContainerDrop(e, mod._id)}
                  >
                    {editingModuleId === mod._id ? (
                      <div className="nn-inline-form" style={{ margin: "4px 8px" }}>
                        <input
                          className="nn-inline-input"
                          autoFocus
                          value={editModuleTitle}
                          onChange={e => setEditModuleTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") handleEditModuleSave(mod); if (e.key === "Escape") setEditingModuleId(null); }}
                        />
                        <div className="nn-inline-row">
                          <button className="nn-inline-save" onClick={() => handleEditModuleSave(mod)}>Save</button>
                          <button className="nn-inline-cancel" onClick={() => setEditingModuleId(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="nn-mod-header"
                        draggable
                        onDragStart={e => handleModuleDragStart(e, mod._id)}
                        onDragEnd={handleModuleDragEnd}
                        onClick={() => handleModuleToggle(mod)}
                      >
                        <span className="nn-drag-grip" onClick={e => e.stopPropagation()}>⠿</span>
                        <span className={`nn-mod-chevron ${mod._open ? "open" : ""}`}>▶</span>
                        <div className="nn-mod-title-wrap">
                          <span className="nn-mod-title" title={mod.title}>{mod.title}</span>
                          {mod._open && <IdBadge id={mod._id} />}
                        </div>
                        {mod._topics !== null && <span className="nn-mod-meta">{mod._topics.length}</span>}
                        <div className="nn-mod-actions" onClick={e => e.stopPropagation()}>
                          <button className="nn-icon-btn" title="Add topic" onClick={() => {
                            if (!mod._open) setModules(prev => prev.map(m => m._id === mod._id ? { ...m, _open: true } : m));
                            if (mod._topics === null) loadTopicsForModule(mod._id);
                            setAddingTopicModuleId(mod._id); setAddingTopicTitle("");
                          }}>＋</button>
                          <button className="nn-icon-btn" title="Insert module above" onClick={() => { setInsertModule({ mode: "above", targetId: mod._id }); setInsertModuleTitle(""); }}>↑</button>
                          <button className="nn-icon-btn" title="Insert module below" onClick={() => { setInsertModule({ mode: "below", targetId: mod._id }); setInsertModuleTitle(""); }}>↓</button>
                          <button className="nn-icon-btn" title="Rename module" onClick={() => { setEditingModuleId(mod._id); setEditModuleTitle(mod.title); }}>✏️</button>
                          <button className="nn-icon-btn del" title="Delete module" onClick={() => handleDeleteModule(mod)}>🗑️</button>
                        </div>
                      </div>
                    )}

                    {mod._open && (
                      <div className="nn-topics-list">
                        {mod._topicsLoading && <div className="nn-tree-loading">Loading…</div>}

                        {(mod._topics || []).map((topic) => (
                          <React.Fragment key={topic._id}>
                            {insertTopic?.mode === "above" && insertTopic.targetId === topic._id && insertTopic.moduleId === mod._id && <InsertTopicForm />}

                            {editingTopicId === topic._id ? (
                              <div className="nn-inline-form" style={{ margin: "2px 0" }}>
                                <input
                                  className="nn-inline-input"
                                  autoFocus
                                  value={editTopicTitle}
                                  onChange={e => setEditTopicTitle(e.target.value)}
                                  onKeyDown={e => { if (e.key === "Enter") handleEditTopicSave(topic, mod._id); if (e.key === "Escape") setEditingTopicId(null); }}
                                />
                                <div className="nn-inline-row">
                                  <button className="nn-inline-save" onClick={() => handleEditTopicSave(topic, mod._id)}>Save</button>
                                  <button className="nn-inline-cancel" onClick={() => setEditingTopicId(null)}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <div
                                className={[
                                  "nn-topic-item",
                                  selectedTopicId === topic._id ? "active" : "",
                                  draggedTopicId === topic._id ? "nn-dragging" : "",
                                  dragOverTopicId === topic._id ? "nn-drag-over" : "",
                                ].join(" ")}
                                draggable
                                onDragStart={e => handleTopicDragStart(e, topic._id, mod._id)}
                                onDragOver={e => handleTopicDragOver(e, topic._id, mod._id)}
                                onDragLeave={() => { if (dragOverTopicId === topic._id) setDragOverTopicId(null); }}
                                onDrop={e => handleTopicDrop(e, topic._id, mod._id)}
                                onDragEnd={handleTopicDragEnd}
                                onClick={() => handleTopicClick(topic, mod)}
                              >
                                <span className="nn-drag-grip" onClick={e => e.stopPropagation()}>⠿</span>
                                <span className="nn-topic-dot" />
                                <div className="nn-topic-label-wrap">
                                  <span className="nn-topic-label" title={topic.title}>{topic.title}</span>
                                  {selectedTopicId === topic._id && <IdBadge id={topic._id} />}
                                </div>
                                <div className="nn-topic-actions" onClick={e => e.stopPropagation()}>
                                  <button className="nn-icon-btn" title="Insert topic above" onClick={() => { setInsertTopic({ mode: "above", targetId: topic._id, moduleId: mod._id }); setInsertTopicTitle(""); }}>↑</button>
                                  <button className="nn-icon-btn" title="Insert topic below" onClick={() => { setInsertTopic({ mode: "below", targetId: topic._id, moduleId: mod._id }); setInsertTopicTitle(""); }}>↓</button>
                                  <button className="nn-icon-btn" title="Rename" onClick={() => { setEditingTopicId(topic._id); setEditTopicTitle(topic.title); }}>✏️</button>
                                  <button className="nn-icon-btn del" title="Delete" onClick={() => handleDeleteTopic(topic, mod._id)}>🗑️</button>
                                </div>
                              </div>
                            )}

                            {insertTopic?.mode === "below" && insertTopic.targetId === topic._id && insertTopic.moduleId === mod._id && <InsertTopicForm />}
                          </React.Fragment>
                        ))}

                        {addingTopicModuleId === mod._id ? (
                          <div className="nn-inline-form" style={{ margin: "4px 0" }}>
                            <input
                              className="nn-inline-input"
                              autoFocus
                              placeholder="New topic name…"
                              value={addingTopicTitle}
                              onChange={e => setAddingTopicTitle(e.target.value)}
                              onKeyDown={e => { if (e.key === "Enter") handleAddTopicSave(mod._id); if (e.key === "Escape") cancelAddTopic(); }}
                            />
                            <div className="nn-inline-row">
                              <button className="nn-inline-save" onClick={() => handleAddTopicSave(mod._id)}>Save</button>
                              <button className="nn-inline-cancel" onClick={cancelAddTopic}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button className="nn-tree-add-btn" onClick={() => { setAddingTopicModuleId(mod._id); setAddingTopicTitle(""); }}>
                            ＋ Add Topic
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {insertModule?.mode === "below" && insertModule.targetId === mod._id && <InsertModuleForm />}
                </React.Fragment>
              ))}

              <div className="nn-add-module-wrap">
                {addingModuleActive ? (
                  <div className="nn-inline-form">
                    <input
                      className="nn-inline-input"
                      autoFocus
                      placeholder="New module name…"
                      value={addingModuleTitle}
                      onChange={e => setAddingModuleTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") handleAddModuleSave(); if (e.key === "Escape") cancelAddModule(); }}
                    />
                    <div className="nn-inline-row">
                      <button className="nn-inline-save" onClick={handleAddModuleSave}>Save</button>
                      <button className="nn-inline-cancel" onClick={cancelAddModule}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="nn-tree-add-btn" style={{ marginLeft: 0 }} onClick={() => setAddingModuleActive(true)}>
                    ＋ Add Module
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── RIGHT PANEL ───────────────────────────────────────────── */}
      <div className="nn-main">
        <div className="nn-main-inner">

          {/* ── Loading section detail ── */}
          {loadingSectionDetail ? (
            <div className="nn-empty-state">
              <div className="nn-tree-loading" style={{ fontSize: 15 }}>Loading section…</div>
            </div>

          /* ── Section detail view (keeps sidebar visible) ── */
          ) : selectedSection ? (
            <>
              {/* Back button + breadcrumb */}
              <div className="nn-section-detail-nav">
                <button className="nn-back-btn" onClick={handleBackToSections}>
                  ← Back to Sections
                </button>
                <div className="nn-content-breadcrumb">
                  <span>{selectedTopic?.subjectName}</span>
                  <span className="sep">›</span>
                  <span>{selectedTopic?.moduleName}</span>
                  <span className="sep">›</span>
                  <span>{selectedTopic?.title}</span>
                  <span className="sep">›</span>
                  <span style={{ color: "#4f46e5", fontWeight: 600 }}>{selectedSection.title}</span>
                </div>
              </div>

              {/* Section header */}
              <div className="nn-content-header">
                <div>
                  <div className="nn-section-detail-meta">
                    <TypeBadge type={selectedSection.type} />
                    <IdBadge id={selectedSection._id} />
                  </div>
                  <h2 className="nn-content-title">{selectedSection.title}</h2>
                </div>
                {selectedSection.type !== "notes" && (
                  <button className="nn-btn-manage" style={{ alignSelf: "center" }} onClick={() => setSectionQSliderOpen(true)}>
                    Manage Questions
                  </button>
                )}
              </div>

              {/* Notes editor */}
              {selectedSection.type === "notes" && (
                <div className="nn-notes-editor-wrap">
                  <RichTextEditor
                    ref={notesEditorRef}
                    value={sectionNotesText}
                    onBlur={val => setSectionNotesText(val)}
                    toolbar="full"
                    height={500}
                    placeholder="Write your notes here…"
                  />
                  <div className="nn-notes-save-row">
                    {savedNoteOk && <span className="nn-saved-ok">✓ Saved</span>}
                    <button className="nn-btn-save-lg" onClick={handleSaveNotes} disabled={savingNotes}>
                      {savingNotes ? "Saving…" : "Save Notes"}
                    </button>
                  </div>
                </div>
              )}

              {/* Questions */}
              {selectedSection.type !== "notes" && (
                <div className="nn-q-panel">
                  {sectionQuestions.length === 0 ? (
                    <p className="nn-q-empty">No questions added yet. Click "Manage Questions" to add some.</p>
                  ) : (
                    <div className="nn-q-list">
                      <div className="nn-section-count">{sectionQuestions.length} question{sectionQuestions.length !== 1 ? "s" : ""}</div>
                      {sectionQuestions.map((q, i) => (
                        <div key={q._id} className="nn-q-row">
                          <span className="nn-q-num">{i + 1}.</span>
                          <div className="nn-q-body">
                            <span className="nn-q-text">{q.question}</span>
                            <div className="nn-q-meta">
                              {q.subject && <span className="nn-q-tag">{q.subject}</span>}
                              <DiffBadge level={q.difficultyLevel} />
                            </div>
                          </div>
                          <button className="nn-btn-del" onClick={() => handleRemoveSectionQuestion(q._id)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <AssignmentQuestionPanel
                open={sectionQSliderOpen}
                onClose={() => setSectionQSliderOpen(false)}
                onConfirm={handleConfirmSectionQuestions}
                existingIds={sectionQIds}
                existingObjects={sectionQuestions}
              />
            </>

          /* ── Empty states ── */
          ) : !selectedTopicId ? (
            <div className="nn-empty-state">
              <div className="nn-empty-icon">📂</div>
              {!selectedSubjectId ? (
                <><h3>Select a subject to get started</h3><p>Choose a subject from the sidebar, then expand modules to find topics.</p></>
              ) : (
                <><h3>Select a topic to view its sections</h3><p>Expand a module in the sidebar and click a topic.</p></>
              )}
            </div>

          /* ── Sections list ── */
          ) : (
            <>
              {/* Topic header */}
              <div className="nn-content-header">
                <div>
                  <div className="nn-content-breadcrumb">
                    <span>{selectedTopic?.subjectName}</span>
                    <span className="sep">›</span>
                    <span>{selectedTopic?.moduleName}</span>
                    <span className="sep">›</span>
                    <span style={{ color: "#4f46e5", fontWeight: 600 }}>{selectedTopic?.title}</span>
                  </div>
                  <h2 className="nn-content-title">
                    {selectedTopic?.title}
                    <IdBadge id={selectedTopicId} />
                  </h2>
                </div>
                <button className="nn-btn-manage" style={{ alignSelf: "center" }} onClick={() => { setSectionInsertAt(null); setAddSectionOpen(true); }}>
                  + Add Section
                </button>
              </div>

              {loadingSections ? (
                <div style={{ color: "#94a3b8", fontSize: 14 }}>Loading sections…</div>
              ) : sections.length === 0 ? (
                <div style={{ color: "#94a3b8", fontSize: 14, paddingTop: 20 }}>
                  No sections yet. Click "+ Add Section" to create one.
                </div>
              ) : (
                <>
                  <div className="nn-section-count">{sections.length} section{sections.length !== 1 ? "s" : ""}</div>
                  {sections.map((section, idx) => (
                    <React.Fragment key={section._id}>
                      <div
                        className={[
                          "nn-section-row",
                          draggedSectionId === section._id ? "nn-dragging" : "",
                          dragOverSectionId === section._id ? "nn-drag-over" : "",
                        ].join(" ")}
                        draggable
                        onDragStart={e => handleSectionDragStart(e, section._id)}
                        onDragOver={e => handleSectionDragOver(e, section._id)}
                        onDragLeave={() => { if (dragOverSectionId === section._id) setDragOverSectionId(null); }}
                        onDrop={e => handleSectionDrop(e, section._id)}
                        onDragEnd={handleSectionDragEnd}
                      >
                        <span className="nn-drag-grip">⠿</span>
                        <span className="nn-section-num">{idx + 1}</span>
                        <div className="nn-section-info">
                          <div className="nn-section-title-row">
                            <span className="nn-section-title">{section.title}</span>
                            <IdBadge id={section._id} />
                          </div>
                          <TypeBadge type={section.type} />
                        </div>
                        <div className="nn-section-actions">
                          <button className="nn-btn-manage" onClick={() => handleOpenSection(section)}>Open</button>
                          <button className="nn-btn-rename" onClick={() => openInsertSectionModal("above", section._id)} title="Insert section above">↑ Insert</button>
                          <button className="nn-btn-rename" onClick={() => openInsertSectionModal("below", section._id)} title="Insert section below">↓ Insert</button>
                          <button className="nn-btn-rename" onClick={() => { setEditingSectionId(section._id); setEditSectionTitle(section.title); setEditSectionError(""); }}>Rename</button>
                          <button className="nn-btn-del" onClick={() => handleDeleteSection(section)}>Delete</button>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── ADD / INSERT SECTION MODAL ────────────────────────────── */}
      {addSectionOpen && (
        <div className="nn-modal-overlay" onClick={cancelAddSection}>
          <div className="nn-modal" onClick={e => e.stopPropagation()}>
            <div className="nn-modal-header">
              <h3 className="nn-modal-title">
                {sectionInsertAt
                  ? `Insert Section ${sectionInsertAt.mode === "above" ? "Above" : "Below"}`
                  : "Add Section"}
              </h3>
              <button className="nn-modal-close" onClick={cancelAddSection}>✕</button>
            </div>
            <div className="nn-modal-body">
              <label className="nn-form-label">Section Title *</label>
              <input
                className="nn-form-input"
                autoFocus
                placeholder="e.g. Introduction Notes, Practice Set 1…"
                value={sectionForm.title}
                onChange={e => { setSectionForm(p => ({ ...p, title: e.target.value })); setSectionFormError(""); }}
                onKeyDown={e => { if (e.key === "Enter") handleAddSectionSave(); }}
              />
              {sectionFormError && <div className="nn-error-msg">{sectionFormError}</div>}
              <label className="nn-form-label" style={{ marginTop: 16 }}>Section Type</label>
              <div className="nn-type-grid">
                {SECTION_TYPES.map(t => {
                  const c = TYPE_COLORS[t.value] || {};
                  const sel = sectionForm.type === t.value;
                  return (
                    <div
                      key={t.value}
                      className="nn-type-chip"
                      style={{ background: sel ? c.bg : "#fff", color: sel ? c.color : "#64748b", borderColor: sel ? c.color : "#e2e8f0" }}
                      onClick={() => setSectionForm(p => ({ ...p, type: t.value }))}
                    >
                      {t.icon} {t.label}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="nn-modal-footer">
              <button className="nn-btn-cancel-lg" onClick={cancelAddSection}>Cancel</button>
              <button className="nn-btn-save-lg" disabled={busySection} onClick={handleAddSectionSave}>
                {busySection ? "Creating…" : sectionInsertAt ? "Insert Section" : "Create Section"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RENAME SECTION MODAL ──────────────────────────────────── */}
      {editingSectionId && (
        <div className="nn-modal-overlay" onClick={cancelRenameSection}>
          <div className="nn-modal" onClick={e => e.stopPropagation()}>
            <div className="nn-modal-header">
              <h3 className="nn-modal-title">Rename Section</h3>
              <button className="nn-modal-close" onClick={cancelRenameSection}>✕</button>
            </div>
            <div className="nn-modal-body">
              <label className="nn-form-label">New Title *</label>
              <input
                className="nn-form-input"
                autoFocus
                value={editSectionTitle}
                onChange={e => { setEditSectionTitle(e.target.value); setEditSectionError(""); }}
                onKeyDown={e => { if (e.key === "Enter") handleRenameSectionSave(); }}
              />
              {editSectionError && <div className="nn-error-msg">{editSectionError}</div>}
            </div>
            <div className="nn-modal-footer">
              <button className="nn-btn-cancel-lg" onClick={cancelRenameSection}>Cancel</button>
              <button className="nn-btn-save-lg" disabled={busySection} onClick={handleRenameSectionSave}>
                {busySection ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CONFIRM DIALOG ────────────────────────────────────────── */}
      <ConfirmationDialog
        show={confirm.show}
        type="delete"
        message={confirm.message}
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(c => ({ ...c, show: false }))}
      />

      {/* ── TOAST ─────────────────────────────────────────────────── */}
      {toast && <div className="nn-toast">{toast}</div>}
    </div>
  );
};

export default NotesManagementPage;
